import React, { useEffect, useState } from "react";
import { formatDateTime } from "../../utils/dateFormat";
import { useNavigate } from "react-router-dom";

// Enhanced Notifications Component with toggle functionality for displaying all or limited notifications
const Notifications = ({
  notifications,
  handleMarkAllAsRead,
  handleMarkAsRead,
}) => {
  const htmlToPlainText = (html = "") => {
    if (!html) return "";

    // If backend sometimes sends already-plain text, keep it fast
    if (!/<[a-z][\s\S]*>/i.test(html)) return html;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body.textContent || "";

    return text
      .replace(/\u00A0/g, " ") // &nbsp;
      .replace(/\s+\n/g, "\n") // clean spacing around newlines
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const navigate = useNavigate();
  const [hasNotifications, setHasNotifications] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const NOTIFICATION_LIMIT = 10; // Number of notifications to show initially

  useEffect(() => {
    setHasNotifications(notifications?.length > 0);
  }, [notifications]);

  // Get the notifications to display based on showAll state
  const getDisplayedNotifications = () => {
    if (!notifications) return [];
    return showAll ? notifications : notifications.slice(0, NOTIFICATION_LIMIT);
  };

  const displayedNotifications = getDisplayedNotifications();
  const hasMoreNotifications = notifications?.length > NOTIFICATION_LIMIT;

  // Toggle between showing all or limited notifications
  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  return (
    <div className="bg-white rounded-lg shadow-xl overflow-hidden min-w-[300px] w-[350px]">
      {/* Arrow indicator pointing to the bell */}
      <div
        className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white absolute -top-2 right-4"
        style={{ marginRight: "4px" }}
      ></div>

      {/* Header with title and mark all as read button */}
      <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200">
        <h2 className="text-lg font-semibold">Notifications</h2>
        <button
          className={`text-indigo-600 text-sm hover:underline ${
            !hasNotifications
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
          }`}
          onClick={hasNotifications ? handleMarkAllAsRead : undefined}
          disabled={!hasNotifications}
        >
          Mark all as read
        </button>
      </div>

      {/* Notification list */}
      <div
        className={`overflow-y-auto ${
          showAll ? "max-h-[600px]" : "max-h-[400px]"
        }`}
      >
        {notifications?.length > 0 ? (
          <ul>
            {displayedNotifications.map((notification) => (
              <li
                key={notification.id}
                className={`px-5 py-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors ${
                  notification.mark_as_read ? "" : "bg-indigo-50"
                }`}
                onClick={() => {
                  handleMarkAsRead(notification.id);

                  const message = notification.message.toLowerCase();

                  if (message.includes("shortlisting file")) {
                    navigate(
                      "/student/documents_upload/shortlisting_of_universities"
                    );
                  } else if (message.includes("shortlisting")) {
                    navigate(
                      "/student/documents_upload/shortlisting_of_universities"
                    );
                  } else if (message.includes("finalized")) {
                    navigate("/student/university_application_process");
                  } else {
                    navigate(notification.link_to || "/"); // fallback route
                  }
                }}
              >
                <div className="flex items-start">
                  <div className="mr-3 mt-1 flex-shrink-0">
                    {!notification.mark_as_read && (
                      <span className="h-3 w-3 bg-indigo-600 rounded-full block"></span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {htmlToPlainText(notification.message)}
                    </div>

                    <div className="text-xs text-gray-500">
                      {formatDateTime(new Date(notification.created_at), {
                        addSuffix: true,
                      })}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-10 px-5 text-center text-gray-500">
            <p>No notifications</p>
          </div>
        )}
      </div>

      {/* Toggle footer */}
      {hasMoreNotifications && (
        <div className="p-3 border-t border-gray-200 text-center bg-gray-50">
          <button
            className="text-indigo-600 text-sm hover:underline"
            onClick={toggleShowAll}
          >
            {showAll ? "Show less" : "View all notifications"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Notifications;
